import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNetworkStore } from '@/stores/network-store';
import { Store, KeyRound, Loader2, CheckCircle2, Monitor, Download, FileSpreadsheet, Cloud } from 'lucide-react';
import api, { clearApiBaseUrlCache } from '@/lib/axios';
import { runCloudSync } from '@/services/cloud-sync.service';

export function SetupScreen() {
  const navigate = useNavigate();
  const { setMode } = useNetworkStore();
  const [token, setToken] = useState('');
  const [registerName, setRegisterName] = useState('Caja 1 - Principal');
  const [registerPrefix, setRegisterPrefix] = useState('C1');
  const [catalogMode, setCatalogMode] = useState<'cloud_pull' | 'blank_usb'>('cloud_pull');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSkipToCloud = () => {
    clearApiBaseUrlCache();
    setMode('cloud');
    navigate('/login', { replace: true });
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      setError('Por favor, ingresa el token de activación.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/branches/activate', {
        token: token.trim(),
        registerName: registerName.trim() || 'Caja 1',
        registerPrefix: registerPrefix.trim() || 'C1',
      });
      const data = response.data;

      // Save activation config to local storage (acts as the identity of this local POS)
      localStorage.setItem('BRANCH_TOKEN', token.trim());
      localStorage.setItem('BRANCH_ID', data.branchId);
      localStorage.setItem('COMPANY_ID', data.companyId);
      localStorage.setItem('BRANCH_NAME', data.branchName);
      localStorage.setItem('COMPANY_NAME', data.companyName);
      localStorage.setItem('REGISTER_NAME', data.registerName || registerName);
      localStorage.setItem('REGISTER_PREFIX', data.registerPrefix || registerPrefix);
      localStorage.setItem('CATALOG_MODE', catalogMode);

      setSuccess(true);
      
      // Delay navigation to show success message & pull catalog if selected
      setTimeout(async () => {
        setMode('local'); // We activate in local mode (working with local db)
        if (catalogMode === 'cloud_pull') {
          try {
            await runCloudSync();
          } catch (syncErr) {
            console.warn('Error bajando catálogo inicial (se continuará en modo local):', syncErr);
          }
        }
        navigate('/login', { replace: true });
      }, 1500);
      
    } catch (err: any) {
      console.error('Error al activar sucursal:', err);
      setError(err.response?.data?.message || 'Error al conectar con la Nube. Verifica el token.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-blue-600 p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 text-white mb-4">
            <Store className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Activación de Caja / Licencia</h1>
          <p className="text-blue-100 text-sm">
            Ingresa el token de licencia para autorizar este equipo y configurar tu terminal.
          </p>
        </div>

        <div className="p-6">
          {success ? (
            <div className="flex flex-col items-center justify-center py-6 text-center animate-in fade-in zoom-in duration-500">
              <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">¡Terminal Activada!</h2>
              <p className="text-gray-500">
                Esta caja ({registerName}) ha sido autorizada correctamente. Redirigiendo...
              </p>
            </div>
          ) : (
            <form onSubmit={handleActivate} className="space-y-5">
              <div>
                <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-1">
                  Token de Activación / Licencia
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="token"
                    value={token}
                    onChange={(e) => setToken(e.target.value.toUpperCase())}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 font-mono text-center text-lg tracking-widest uppercase transition-colors"
                    placeholder="KAJA-XXXX-XXXX"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Selector de Caja / Terminal */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                    <Monitor className="w-3.5 h-3.5 mr-1 text-gray-500" />
                    Nombre de Caja
                  </label>
                  <select
                    value={registerName}
                    onChange={(e) => {
                      setRegisterName(e.target.value);
                      if (e.target.value.includes('Caja 1')) setRegisterPrefix('C1');
                      else if (e.target.value.includes('Caja 2')) setRegisterPrefix('C2');
                      else if (e.target.value.includes('Caja 3')) setRegisterPrefix('C3');
                    }}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-blue-500"
                    disabled={isLoading}
                  >
                    <option value="Caja 1 - Principal">Caja 1 - Principal</option>
                    <option value="Caja 2 - Rápida">Caja 2 - Rápida</option>
                    <option value="Caja 3 - Secundaria">Caja 3 - Secundaria</option>
                    <option value="Caja Personalizada">Caja Personalizada</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Prefijo
                  </label>
                  <input
                    type="text"
                    value={registerPrefix}
                    onChange={(e) => setRegisterPrefix(e.target.value.toUpperCase())}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-xl text-center font-mono font-bold"
                    maxLength={5}
                    placeholder="C1"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Modo de Inventario Inicial */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Modo de Inventario Inicial
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCatalogMode('cloud_pull')}
                    className={`p-3 border rounded-xl flex flex-col items-center justify-center text-center transition-all ${
                      catalogMode === 'cloud_pull'
                        ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <Download className="w-5 h-5 mb-1" />
                    <span className="text-xs">Bajar de la Nube</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatalogMode('blank_usb')}
                    className={`p-3 border rounded-xl flex flex-col items-center justify-center text-center transition-all ${
                      catalogMode === 'blank_usb'
                        ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <FileSpreadsheet className="w-5 h-5 mb-1" />
                    <span className="text-xs">Importar por USB / Excel</span>
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md animate-in slide-in-from-top-2">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Validando Licencia...
                  </>
                ) : (
                  'Activar Terminal'
                )}
              </button>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-gray-400">o</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSkipToCloud}
                disabled={isLoading}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all"
              >
                <Cloud className="w-5 h-5 text-blue-600" />
                Continuar en modo nube (sin licencia)
              </button>
              <p className="text-xs text-center text-gray-400">
                Requiere la API Nest corriendo en el puerto 3001.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

